import { z } from "zod";

export const automationActionSchema = z.enum(["create", "update", "delete"]);
export const automationCapabilitySchema = z.object({
  objectType: z.string(),
  action: automationActionSchema,
});
export const automationModelSchema = z.enum(["gpt-6.1-sol", "gpt-6-luna"]);
export type AutomationModel = z.infer<typeof automationModelSchema>;
export const automationScopeSchema = z.enum(["firm", "user"]);
export type AutomationScope = z.infer<typeof automationScopeSchema>;
export const automationConfigSchema = z.object({
  model: automationModelSchema,
  scope: automationScopeSchema,
  uploadedByUserIds: z.array(z.string()).max(200),
  name: z.string().trim().min(1, "Give this automation a name.").max(200),
  instructions: z
    .string()
    .trim()
    .min(1, "Describe what this automation should do.")
    .max(50000),
  tagIds: z.array(z.string()).min(1, "Choose at least one tag."),
  tagMatchMode: z.enum(["any", "all"]),
  capabilities: z.array(automationCapabilitySchema),
  enabled: z.boolean(),
});
export const automationSchema = automationConfigSchema.extend({
  model: automationModelSchema.default("gpt-6.1-sol"),
  ownerUserId: z.string().nullable(),
  id: z.string(),
  currentRevisionId: z.string(),
  version: z.number(),
  origin: z.string(),
  dateCreated: z.string(),
  dateModified: z.string(),
});
export const contentTagInputSchema = z.object({
  name: z.string().trim().min(1, "Give this tag a name.").max(200),
  description: z
    .string()
    .trim()
    .min(1, "Describe what this tag means.")
    .max(10000),
  applicationGuidance: z.string().max(10000).nullable(),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
});
export const contentTagSchema = contentTagInputSchema.extend({
  id: z.string(),
  dateCreated: z.string(),
  dateModified: z.string(),
});
export const capabilityOptionSchema = z.object({
  objectType: z.string(),
  label: z.string(),
  actions: z.array(automationActionSchema),
});
export const automationRunSchema = z.object({
  id: z.string(),
  fileContentId: z.string().nullable(),
  fileName: z.string().nullable(),
  threadId: z.string().nullable(),
  harnessRunId: z.string().nullable(),
  status: z.string(),
  triggerType: z.string(),
  dateCreated: z.string(),
});
export type Automation = z.infer<typeof automationSchema>;
export type AutomationConfig = z.infer<typeof automationConfigSchema>;
export type ContentTag = z.infer<typeof contentTagSchema>;
export type ContentTagInput = z.infer<typeof contentTagInputSchema>;
export type CapabilityOption = z.infer<typeof capabilityOptionSchema>;
export type AutomationRun = z.infer<typeof automationRunSchema>;
export type AutomationAction = z.infer<typeof automationActionSchema>;

export const automationFileSchema = z.object({
  id: z.string(),
  fileName: z.string(),
  sourceKind: z.enum(["matter", "firm"]),
  matterId: z.string().nullable(),
  massTortId: z.string().nullable(),
  matterFileId: z.string().nullable(),
  firmFileId: z.string().nullable(),
  documentDescription: z.string().nullable(),
  processingId: z.string().nullable(),
  pageCount: z.number().int().nonnegative().nullish(),
  status: z.string(),
});

export const automationSummarySchema = automationSchema.pick({
  scope: true,
  ownerUserId: true,
  id: true,
  name: true,
  enabled: true,
  origin: true,
  currentRevisionId: true,
  version: true,
  dateCreated: true,
  dateModified: true,
});
export type AutomationSummary = z.infer<typeof automationSummarySchema>;
